// src/components/laboratory/tabs/AdsTab.jsx
import React, { useState } from 'react';
import { 
  Plus, 
  Megaphone, 
  Search, 
  PencilLine, 
  Trash2, 
  Calendar,
  CheckCircle,
  XCircle,
  Clock,
  Package,
  Filter,
  Eye,
  AlertCircle
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useMutation } from '@apollo/client';
import { DELETE_ADD } from '../../../api/laboratory-api';
import { toast } from 'react-toastify';
import ModalComponent from '../../common/ModalComponent';

const AdsTab = ({ 
  adds = [], 
  loading, 
  onAddAd, 
  onEditAd, 
  onDeleteAd,
  laboratory,
  products = [],
  refetchAds
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedAd, setSelectedAd] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [adToDelete, setAdToDelete] = useState(null);

  // Mutation for deleting ads
  const [deleteAdd, { loading: deleteLoading }] = useMutation(DELETE_ADD, {
    onCompleted: (data) => {
      if (data.removeAdd) {
        toast.success('Publicité supprimée avec succès');
        refetchAds();
      }
    },
    onError: (error) => {
      toast.error(`Erreur lors de la suppression: ${error.message}`);
    }
  });

  // Get status of an ad based on dates
  const getAdStatus = (ad) => {
    const now = new Date();
    const startDate = new Date(ad.dateDebut);
    const endDate = new Date(ad.dateFin);

    if (now < startDate) {
      return {
        status: 'pending',
        text: 'À venir',
        className: 'bg-yellow-100 text-yellow-800',
        icon: Clock
      };
    } else if (now > endDate) {
      return {
        status: 'expired',
        text: 'Expirée',
        className: 'bg-red-100 text-red-800',
        icon: XCircle
      };
    } else {
      return {
        status: 'active',
        text: 'En cours',
        className: 'bg-green-100 text-green-800',
        icon: CheckCircle
      };
    }
  };

  // Filter ads based on search and status
  const filteredAds = adds.filter(ad => {
    const matchesSearch = ad.product?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         ad.laboratory?.nom?.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (statusFilter === 'all') return matchesSearch;
    
    const adStatus = getAdStatus(ad);
    return matchesSearch && adStatus.status === statusFilter;
  });

  // Group ads by status for summary
  const adsSummary = adds.reduce((acc, ad) => {
    const status = getAdStatus(ad).status;
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});

  // Handle ad view
  const handleViewAd = (ad) => {
    setSelectedAd(ad);
    setIsViewModalOpen(true);
  };

  // Handle edit click
  const handleEditClick = (e, ad) => {
    e.stopPropagation();
    onEditAd(ad);
  };

  // Handle delete click
  const handleDeleteClick = (e, ad) => {
    e.stopPropagation();
    setAdToDelete(ad);
    setIsConfirmDeleteOpen(true);
  };

  // Handle confirm delete
  const handleConfirmDelete = async () => {
    if (adToDelete) {
      try {
        await deleteAdd({
          variables: { id: adToDelete.id }
        });
        setIsConfirmDeleteOpen(false);
        setAdToDelete(null);
      } catch (error) {
        console.error('Error deleting ad:', error);
      }
    }
  };

  // Calculate ad duration in days
  const getAdDuration = (ad) => {
    const startDate = new Date(ad.dateDebut);
    const endDate = new Date(ad.dateFin);
    return Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
  };

  return (
    <div className="space-y-6">
      {/* Header with statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <div className="flex items-center">
            <Megaphone className="h-8 w-8 text-blue-600 mr-3" />
            <div>
              <p className="text-sm font-medium text-blue-600">Total</p>
              <p className="text-2xl font-bold text-blue-800">{adds.length}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
          <div className="flex items-center">
            <CheckCircle className="h-8 w-8 text-green-600 mr-3" />
            <div>
              <p className="text-sm font-medium text-green-600">En cours</p>
              <p className="text-2xl font-bold text-green-800">{adsSummary.active || 0}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
          <div className="flex items-center">
            <Clock className="h-8 w-8 text-yellow-600 mr-3" />
            <div>
              <p className="text-sm font-medium text-yellow-600">À venir</p>
              <p className="text-2xl font-bold text-yellow-800">{adsSummary.pending || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-red-50 p-4 rounded-lg border border-red-200">
          <div className="flex items-center">
            <XCircle className="h-8 w-8 text-red-600 mr-3" />
            <div>
              <p className="text-sm font-medium text-red-600">Expirées</p>
              <p className="text-2xl font-bold text-red-800">{adsSummary.expired || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Actions and filters */}
      <div className="flex flex-col space-y-4 sm:flex-row sm:space-y-0 sm:space-x-4 sm:items-center sm:justify-between">
        <div className="flex-1">
          <div className="flex flex-col space-y-2 sm:flex-row sm:space-y-0 sm:space-x-3">
            {/* Search */}
            <div className="relative flex-1">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Rechercher par produit ou laboratoire..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Status filter */}
            <div className="relative w-full sm:w-48">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Filter className="h-5 w-5 text-gray-400" />
              </div>
              <select
                className="block w-full pl-10 pr-10 py-2 text-base border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">Tous les statuts</option>
                <option value="active">En cours</option>
                <option value="pending">À venir</option>
                <option value="expired">Expirées</option>
              </select>
            </div>
          </div>
        </div>

        {/* Add button */}
        <button
          onClick={onAddAd}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <Plus className="-ml-1 mr-2 h-5 w-5" />
          Nouvelle publicité
        </button>
      </div>

      {/* Loading state */}
      {loading && (
        <div className="text-center py-12">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
          <p className="mt-2 text-gray-500">Chargement des publicités...</p>
        </div>
      )}

      {/* Empty state */}
      {!loading && filteredAds.length === 0 && (
        <div className="text-center py-12 bg-white border border-gray-200 rounded-lg">
          <Megaphone size={48} className="mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {adds.length === 0 ? 'Aucune publicité' : 'Aucun résultat'}
          </h3>
          <p className="text-gray-500 mb-4">
            {adds.length === 0 
              ? "Ce laboratoire n'a pas encore de publicités." 
              : "Aucune publicité ne correspond à vos critères de recherche."}
          </p>
          {adds.length === 0 && (
            <button
              onClick={onAddAd}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="-ml-1 mr-2 h-4 w-4" />
              Créer votre première publicité
            </button>
          )}
          {adds.length > 0 && (
            <button
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('all');
              }}
              className="text-blue-600 hover:text-blue-500"
            >
              Réinitialiser les filtres
            </button>
          )}
        </div>
      )}

      {/* Ads list */}
      {!loading && filteredAds.length > 0 && (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Publicités ({filteredAds.length})
            </h3>
            
            <div className="space-y-4">
              {filteredAds.map((ad) => {
                const statusInfo = getAdStatus(ad);
                const StatusIcon = statusInfo.icon;
                const duration = getAdDuration(ad);

                return (
                  <div
                    key={ad.id}
                    className={`border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer ${
                      statusInfo.status === 'active' ? 'border-green-300 bg-green-50' :
                      statusInfo.status === 'pending' ? 'border-yellow-300 bg-yellow-50' :
                      'border-red-300 bg-red-50'
                    }`}
                    onClick={() => handleViewAd(ad)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4 flex-1">
                        {/* Product image */}
                        <div className="flex-shrink-0">
                          {ad.product?.image ? (
                            <img
                              className="h-16 w-16 rounded-lg object-cover"
                              src={ad.product.image}
                              alt={ad.product.name}
                            />
                          ) : (
                            <div className="h-16 w-16 rounded-lg bg-gray-200 flex items-center justify-center">
                              <Package className="h-8 w-8 text-gray-400" />
                            </div>
                          )}
                        </div>

                        {/* Ad details */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 mb-2">
                            <h4 className="text-lg font-medium text-gray-900 truncate">
                              {ad.product?.name || 'Produit inconnu'}
                            </h4>
                            <StatusIcon className="h-5 w-5 text-gray-400" />
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusInfo.className}`}>
                              {statusInfo.text}
                            </span>
                          </div>

                          <div className="flex items-center space-x-6 text-sm text-gray-600">
                            <div className="flex items-center">
                              <Calendar className="h-4 w-4 mr-1" />
                              <span>
                                {format(new Date(ad.dateDebut), 'dd/MM/yyyy', { locale: fr })} - {' '}
                                {format(new Date(ad.dateFin), 'dd/MM/yyyy', { locale: fr })}
                              </span>
                            </div>
                            
                            <div className="flex items-center">
                              <Clock className="h-4 w-4 mr-1" />
                              <span>{duration} jour{duration > 1 ? 's' : ''}</span>
                            </div>

                            {ad.product?.price && (
                              <div className="flex items-center">
                                <span className="font-medium text-green-600">
                                  {ad.product.price.toFixed(2)} DH
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center space-x-2 ml-4">
                        <button
                          onClick={() => handleViewAd(ad)}
                          className="text-blue-600 hover:text-blue-900 p-2 rounded-full hover:bg-blue-100"
                          title="Voir les détails"
                        >
                          <Eye size={18} />
                        </button>
                        
                        <button
                          onClick={(e) => handleEditClick(e, ad)}
                          className="text-indigo-600 hover:text-indigo-900 p-2 rounded-full hover:bg-indigo-100"
                          title="Modifier"
                        >
                          <PencilLine size={18} />
                        </button>
                        
                        <button
                          onClick={(e) => handleDeleteClick(e, ad)}
                          className="text-red-600 hover:text-red-900 p-2 rounded-full hover:bg-red-100"
                          title="Supprimer"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* View Ad Modal */}
      {selectedAd && (
        <ModalComponent
          isOpen={isViewModalOpen}
          onClose={() => {
            setIsViewModalOpen(false);
            setSelectedAd(null);
          }}
          title="Détails de la publicité"
          size="lg"
          showFooter={false}
        >
          <div className="space-y-6">
            {/* Product info */}
            <div className="flex items-center space-x-4">
              {selectedAd.product?.image ? (
                <img
                  className="h-20 w-20 rounded-lg object-cover"
                  src={selectedAd.product.image}
                  alt={selectedAd.product.name}
                />
              ) : (
                <div className="h-20 w-20 rounded-lg bg-gray-200 flex items-center justify-center">
                  <Package className="h-10 w-10 text-gray-400" />
                </div>
              )}
              
              <div>
                <h3 className="text-xl font-semibold text-gray-900">
                  {selectedAd.product?.name || 'Produit inconnu'}
                </h3>
                {selectedAd.product?.price && (
                  <p className="text-lg font-medium text-green-600">
                    {selectedAd.product.price.toFixed(2)} DH
                  </p>
                )}
              </div>
            </div>

            {/* Status and dates */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Statut</h4>
                <div className="flex items-center space-x-2">
                  {React.createElement(getAdStatus(selectedAd).icon, { 
                    className: "h-5 w-5 text-gray-500" 
                  })}
                  <span className={`px-3 py-1 text-sm font-medium rounded-full ${getAdStatus(selectedAd).className}`}>
                    {getAdStatus(selectedAd).text}
                  </span>
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Durée</h4>
                <p className="text-lg font-semibold text-gray-900">
                  {getAdDuration(selectedAd)} jour{getAdDuration(selectedAd) > 1 ? 's' : ''}
                </p>
              </div>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date de début</label>
                <p className="text-sm text-gray-900">
                  {format(new Date(selectedAd.dateDebut), 'dd MMMM yyyy', { locale: fr })}
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date de fin</label>
                <p className="text-sm text-gray-900">
                  {format(new Date(selectedAd.dateFin), 'dd MMMM yyyy', { locale: fr })}
                </p>
              </div>
            </div>

            {/* Creation info */}
            <div className="pt-4 border-t border-gray-200">
              <p className="text-sm text-gray-500">
                Créée le {format(new Date(selectedAd.createdAt), 'dd MMMM yyyy à HH:mm', { locale: fr })}
              </p>
            </div>
          </div>
        </ModalComponent>
      )}

      {/* Confirm Delete Modal */}
      <ModalComponent
        isOpen={isConfirmDeleteOpen}
        onClose={() => setIsConfirmDeleteOpen(false)}
        title="Confirmer la suppression"
        onSubmit={handleConfirmDelete}
        submitLabel="Supprimer"
        submitButtonClassName="bg-red-600 hover:bg-red-700 focus:ring-red-500"
        isSubmitting={deleteLoading}
        size="md"
      >
        <div className="space-y-4">
          <div className="flex items-center space-x-3 text-red-600">
            <AlertCircle size={24} />
            <h3 className="text-lg font-medium">Attention</h3>
          </div>
          
          <p className="text-gray-700">
            Êtes-vous sûr de vouloir supprimer cette publicité pour le produit{' '}
            <strong>{adToDelete?.product?.name}</strong> ?
          </p>
          
          <div className="bg-yellow-50 p-3 rounded border border-yellow-100">
            <p className="text-sm text-yellow-800">
              Cette action est irréversible. La publicité sera définitivement supprimée.
            </p>
          </div>
        </div>
      </ModalComponent>
    </div>
  );
};

export default AdsTab;